import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { LayoutGrid, Ticket, Sparkles } from "lucide-react";
import InnerLayout from "../../../components/layouts/inner-layout";
import { DataContext } from "../../../components/layouts/main-layout";
import errorToast from "../../../components/helper-components/error-toast";
import successToast from "../../../components/helper-components/success-toast";
import { isAdmin } from "../../../redux/config";
import {
  couponListAction,
  couponPauseAction,
  couponResumeAction,
  couponArchiveAction,
} from "../../../redux/actions/coupon-action";
import OverviewTab from "./tabs/overview-tab";
import ListTab from "./tabs/list-tab";
import CreateTab from "./tabs/create-tab";

const TAB_IDS = ["overview", "list", "create"];

// "create" is a transient view, not a sub-tab — entered via the "+ Create" button
// on the list page or via openEdit() for editing a row. Keeping it out of the
// visible tab list means the form opens as an inline drill-down rather than as
// a parallel destination the user can navigate to with stale state.
const TABS = [
  { id: "overview", label: "Overview", Icon: LayoutGrid },
  { id: "list", label: "Coupons", Icon: Ticket },
];

const HASH_TAB = (h) => {
  const v = (h || "").replace("#", "");
  return TAB_IDS.includes(v) ? v : "overview";
};

const SubTabs = ({ active, onChange }) => (
  <div className="flex items-center gap-1 px-6 border-b border-[#E6E9EF] overflow-x-auto bg-white">
    {TABS.map((t) => {
      const isActive = active === t.id;
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`flex items-center gap-2 px-3.5 py-3 text-[13px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
            isActive
              ? "border-primary text-primary"
              : "border-transparent text-[#64748B] hover:text-[#334155]"
          }`}
        >
          <t.Icon size={16} />
          {t.label}
        </button>
      );
    })}
  </div>
);

const CouponsAdmin = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permissionFn } = useContext(DataContext);

  const [tab, setTab] = useState(() => HASH_TAB(window.location.hash));
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const perm = permissionFn ? permissionFn("coupon_admin") || {} : { all: true };

  const loadList = () => {
    setLoading(true);
    dispatch(
      couponListAction(
        {},
        (res) => {
          setLoading(false);
          setCoupons(Array.isArray(res?.data) ? res.data : []);
        },
        (err) => {
          setLoading(false);
          setCoupons([]);
          if (err?.message) errorToast(err.message);
        }
      )
    );
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onHash = () => setTab(HASH_TAB(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const goTo = (t) => {
    setTab(t);
    window.location.hash = t;
    document.querySelector(".coupons-scroller")?.scrollTo(0, 0);
  };

  const openEdit = (row) => {
    setEditing(row);
    goTo("create");
  };

  // Lifecycle: pause → coupon stops validating at the till; resume
  // re-activates; archive = soft delete (kept for audit, never evaluated).
  // Writes are CPOS-only, same as scheme admin.
  const onStatusChange = (id, action) => {
    const dispatchAction =
      action === "paused"
        ? couponPauseAction
        : action === "active"
        ? couponResumeAction
        : couponArchiveAction;
    dispatch(
      dispatchAction(
        { id },
        () => {
          successToast(
            action === "paused"
              ? "Coupon paused"
              : action === "active"
              ? "Coupon resumed"
              : "Coupon archived"
          );
          loadList();
        },
        (err) => errorToast(err?.message || "Couldn't update coupon status")
      )
    );
  };

  const onSavedReload = () => {
    setEditing(null);
    loadList();
    goTo("list");
  };

  if (perm.all === false) {
    const denied = (
      <div className="h-full flex items-center justify-center text-[#64748B]">
        You don't have permission to view Coupons.
      </div>
    );
    return embedded ? denied : <InnerLayout title="Settings">{denied}</InnerLayout>;
  }

  const body = (
    <div className={`flex flex-col ${embedded ? "" : "h-full bg-[#FAFBFC]"}`}>
      {!embedded && (
        <div className="flex items-center gap-2 px-6 py-3 text-[13px] text-[#475467]">
          <button
            className="flex items-center gap-1 hover:text-[#0F172A]"
            onClick={() => navigate("/settings")}
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Discount Settings
          </button>
          <span>/</span>
          <span className="text-[#0F172A] font-medium">Coupons</span>
        </div>
      )}

      <SubTabs active={tab} onChange={goTo} />

        <div className="coupons-scroller flex-1 overflow-y-auto">
          <div className="px-6 py-5 max-w-[1400px]">
            {tab === "overview" ? (
              <OverviewTab coupons={coupons} loading={loading} goTo={goTo} />
            ) : tab === "list" ? (
              <ListTab
                coupons={coupons}
                loading={loading}
                readOnly={!isAdmin}
                onCreate={() => {
                  setEditing(null);
                  goTo("create");
                }}
                onEdit={openEdit}
                onRefresh={loadList}
                onStatusChange={onStatusChange}
              />
            ) : tab === "create" ? (
              <CreateTab
                editing={editing}
                onCancel={() => {
                  setEditing(null);
                  goTo("list");
                }}
                onSaved={onSavedReload}
              />
            ) : null}
          </div>
        </div>
    </div>
  );

  return embedded ? body : <InnerLayout title="Settings">{body}</InnerLayout>;
};

export default CouponsAdmin;
